/* Copyright (c) 2020-present CloudIO Inc */

import { getManager } from '../../registry/Manager';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue, preCompile } from './LocalDataStore';
import {
  controlledBy,
  displayName,
  dsAppUidProp,
  getNextAlias,
  hideDirtyIndicator,
} from '../DataStore/DataStoreManager';
import { camelCase } from 'lodash-es';

const LocalDataStoreManager: ComponentManagerFn<'LocalDataStore'> = (
  platform: Platform,
) => {
  let timer: NodeJS.Timeout;
  return {
    ...baseComponentManager<'LocalDataStore'>(platform, 'LocalDataStore'),
    type: 'LocalDataStore',
    name: 'Local Data Store',
    isChildAllowed: (page, item, childType) =>
      (['TriggerBeforeCreate', 'TriggerOnInitialize'] as ItemType[]).includes(
        childType,
      ),
    defaultValue,
    props: {
      dsAppUid: dsAppUidProp(platform),
      dataSource: {
        label: 'Data Source',
        type: 'string',
        required: true,
      },
      alias: {
        label: 'Data Source Alias',
        type: 'DataSourceAlias',
        required: true,
      },
      displayName,
      dataFormat: {
        label: 'Data Format',
        type: 'select',
        options: { csv: 'CSV', json: 'JSON', ts: 'Typescript' },
        required: true,
      },
      data: {
        label: 'CSV Data',
        type: 'csv',
        required: (item: RequiredItem<'LocalDataStore'>) =>
          item.props.dataFormat === 'csv',
      },
      jsonData: {
        label: 'JSON Data',
        type: 'json',
        required: (item: RequiredItem<'LocalDataStore'>) =>
          item.props.dataFormat === 'json',
      },
      tsData: {
        label: 'Typescript',
        type: 'typescript',
        required: (item: RequiredItem<'LocalDataStore'>) =>
          item.props.dataFormat === 'ts',
        defaultScript: `
return [
    {
        firstName: "John",
        number: 123.456,
        dateTime: new Date().toISOString(),
        boolean: true,
        checkbox: "Y"
    }
];
`,
        preCompile,
      },
      discardInitialData: {
        label: 'Discard Initial Data',
        type: 'boolean',
        helpText:
          'Check this to NOT populate the data store with initial data.',
      },
      insertable: {
        label: 'Insertable',
        type: 'booleanExpression',
      },
      updatable: {
        label: 'Updatable',
        type: 'booleanExpression',
      },
      deletable: {
        label: 'Deletable',
        type: 'boolean',
      },
      singleton: {
        label: 'Singleton',
        type: 'boolean',
        helpText:
          'Singleton dataStores defined across different pages, with the same alias, will share the same state.',
      },
      ignorePKDuplicate: {
        label: 'Ignore Primary Keys Duplicates',
        type: 'boolean',
        helpText:
          'Check this to ignore the duplicate primary key values on this store.',
      },
      controlledBy,
      hideDirtyIndicator,
    },
    isPropertyVisible: (property, item) => {
      if (property === 'dsAppUid') {
        return false;
      }
      switch (property) {
        case 'controlledBy':
          return false;
        case 'dataSource':
          return true;
        case 'alias':
          return !!item.dataSource;
        case 'jsonData':
          return !!item.alias && item.dataFormat === 'json';
        case 'tsData':
          return !!item.alias && item.dataFormat === 'ts';
        case 'data':
          return !!item.alias && item.dataFormat === 'csv';
        default:
          return !!item.alias;
      }
    },
    onPropertyChange: (property, value, itemProps, { appUid, pageId }) => {
      if (property === 'dataSource' && typeof value === 'string') {
        const map = platform.getDataSources(appUid, pageId);
        const val = getNextAlias(`${camelCase(value)}Alias`, map);
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          platform.resetAliases(appUid, pageId);
        }, 1300);
        return {
          [property]: value,
          alias: val,
        };
      }
      if (property === 'dataFormat' && value !== itemProps.dataFormat) {
        return {
          [property]: value,
          jsonData: '',
          data: '',
          tsData: '',
        };
      }
      return {
        [property]: value,
      };
    },
    onCopy: (props, samePage) => ({
      ...props,
      alias: samePage ? '' : props.alias,
    }),
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      if (!item.props.alias) return '';
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isDataSourceAliasUsed(itm, item.props.alias);
      });
      return usage.length
        ? `${item.props.alias} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.alias;
    },
    generateShortId: (item) => item.alias,
    replaceableWith: ['ReadOnlyDataStore', 'DataStore', 'CloudDataStore'],
    category: 'Data',
    icon: { iconName: 'database' },
    description: `**Overview**<br/>
The Local Data Store is a client-side, in-memory data container that allows you to define and manage data directly within the page, without connecting to a backend database or API.
It acts exactly like a standard Data Store—it has an alias, supports CRUD operations, and binds to UI components—but its data is transient and lives only in the browser's memory. This makes it the perfect tool for rapid prototyping, building "Proof of Concept" dashboards, or managing temporary state (like a shopping cart) that doesn't need immediate persistence.

**1. Data Definition Formats**<br/>
Unlike backend stores that rely on database schemas, the Local Data Store lets you define your data structure on the fly using three formats:
* **CSV (Comma Separated Values):** Best for simple, tabular data. (Use Case: Quickly pasting a list of "Employee Names" or "Product Categories" from a spreadsheet to populate a dropdown).
* **JSON:** Best for structured or nested data. (Use Case: Mocking a complex API response with nested objects to test a Detail View layout).
* **TypeScript:** The most powerful option. It allows you to write a function that returns an array of objects. (Use Case: Generating dynamic mock data or creating large datasets programmatically using loops).

**2. Configuration & Identity**<br/>
* **Data Source Name:** Even though there is no real backend, you must provide a name (e.g., "MockEmployees"). This is used to generate the Alias.
* **Data Source Alias:** The handle used by UI components to bind to this store.
* **Strategy:** Name your alias "employeeStore". When you eventually replace this Local Data Store with a real Data Store, you can keep the alias "employeeStore", and all your UI bindings (Forms, Grids) will continue to work without modification.

**3. Permissions (Simulation)**<br/>
To test how your UI behaves under different security scenarios, the Local Data Store allows you to simulate permissions.
* **Insertable / Updatable / Deletable:** Boolean expressions.
* **Effect:** Even though the data is local, setting **Updatable: false** will cause any connected Form or Grid to switch to "Read-Only" mode. This allows you to perfect your UI's logic before the real backend security rules are ready.

**4. Lifecycle & Scope**<br/>
* **Singleton:**
    * **Enabled:** The store's data is shared across all pages that use the same Alias. Useful for "Global State" like user preferences or a multi-step wizard.
    * **Disabled:** Each page gets a fresh, isolated copy of the data.
* **Discard Initial Data:** If enabled, the store initializes with an empty dataset instead of the CSV/JSON you provided. Use this if you want to define the structure (columns) via the mockup but populate the rows dynamically at runtime using triggers.

**5. Transitioning to Production**<br/>
The Local Data Store is designed to be temporary. Once your backend schema is finalized:
1. Use the **Replace With** feature in the designer.
2. Select **Data Store** (Database) or **Cloud Data Store** (API).
3. The system swaps the component but preserves the **Alias**, ensuring your UI remains intact.`,
  };
};

export default LocalDataStoreManager;
